import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useAuth } from 'react-oidc-context';

import config from 'config';
import { set_logged_in, set_menu_open } from 'store/actions';
import { rememberReturnTo, suspendReturnTo } from 'utils/returnTo';

// Router-relative path of the page in the address bar. Read from
// window.location at call time rather than useLocation(): that would make
// logout() change identity on every navigation (see the note below).
const getCurrentPath = () => {
  const { pathname, search, hash } = window.location;
  return `${pathname.slice(config.basename.length)}${search}${hash}`;
};

const useLogout = () => {
  const dispatch = useDispatch();
  const auth = useAuth();

  // Memoized: callers depend on this in their own useCallback/useEffect deps
  // (e.g. dashboard data-fetch hooks) - an unstable reference here cascades
  // into those being recreated every render, which breaks anything timing-
  // sensitive downstream (intervals get torn down and restarted constantly).
  //
  // Almost every caller is an involuntary session end (401 from the API,
  // failed silent renew), so by default the current page is remembered and
  // the user is sent back to it after signing in again. A deliberate sign-out
  // passes returnToCurrentPage: false - the next person to log in on this tab
  // shouldn't be dropped onto the previous user's page.
  const logout = useCallback(
    ({ returnToCurrentPage = true } = {}) => {
      if (returnToCurrentPage) {
        rememberReturnTo(getCurrentPath());
      } else {
        suspendReturnTo();
      }

      localStorage.removeItem('isLoggedIn');
      localStorage.removeItem('token');
      localStorage.removeItem('username');
      dispatch(set_logged_in(false));
      dispatch(set_menu_open('default'));
      // Ends the Keycloak session too and redirects back to /login —
      // no local navigate() needed, this leaves the app entirely.
      auth.signoutRedirect();
    },
    [dispatch, auth]
  );

  return { logout };
};

export default useLogout;
